import React from 'react';
import PropTypes from 'prop-types';
import RaisedButton from 'material-ui/RaisedButton';
import CircularProgress from 'material-ui/CircularProgress';
import IconButton from 'material-ui/IconButton';
import EditIcon from 'material-ui/svg-icons/editor/mode-edit';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import OverlayController from '../overlay/OverlayController';
import Dialog from '../overlay/Dialog';
import PluginDialog from './PluginDialog';
import PluginsManager from './PluginsManager';
import queries from '../../graphql/queries';
import mutations from '../../graphql/mutations';
import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table';
import {Direction, withReorder} from "../withReorder/WithReorder";
import { compose } from 'react-apollo';
import { GraphQLStackTrace, getValueAtPathOfClosestParent } from "../../utils/utils";
import _ from 'lodash';
import './Plugins.css'


export default compose(
    queries.projectQueryHoc,
    mutations.deletePluginMutationHoc,
    mutations.updatePluginsOrderMutationHoc,
    withReorder({direction: Direction.Vertical, itemSize: 48})
)(class extends React.Component {

    static propTypes = {
        data: PropTypes.object.isRequired,
        deletePlugin: PropTypes.func.isRequired,
        updatePluginsOrder: PropTypes.func.isRequired,

        beginReorder: PropTypes.func.isRequired,
        resetReorder: PropTypes.func.isRequired,
        onMouseDown: PropTypes.func.isRequired,
        indexOfItem: PropTypes.func.isRequired,
        reordering: PropTypes.bool.isRequired,
        reorderItems: PropTypes.array,
        draggedItem: PropTypes.any,
        translateX: PropTypes.number,
        translateY: PropTypes.number
    };

    static defaultProps = {};

    onSortClick = () => {
        let pluginIds = this.props.data.project.plugins.map(plugin => plugin.id);
        this.props.beginReorder(pluginIds);
    };

    onSaveSortClick = () => {
        let projectId = this.props.data.project.id;
        let pluginIds = this.props.reorderItems;

        OverlayController.showActivityIndicator();
        this.props.updatePluginsOrder(projectId, pluginIds).then(() => {
            OverlayController.hideActivityIndicator();
            this.props.resetReorder();
        }).catch(error => {
            OverlayController.hideActivityIndicator();
            OverlayController.showErrorAlert(error);
            this.props.resetReorder()
        });
    };

    onCancelSortClick = () => {
        this.props.resetReorder()
    };

    didSelectAddPlugin() {
        OverlayController.showOverlay({
            component: <PluginDialog project={this.props.data.project} />,
            className: 'pluginDialogOverlay'
        });
    }

    didSelectEditPlugin = (event) => {
        let pluginId = getValueAtPathOfClosestParent(event.currentTarget, 'dataset.pluginId');
        let plugin = _.find(this.props.data.project.plugins, {id: pluginId});
        OverlayController.showOverlay({
            component: <PluginDialog project={this.props.data.project} plugin={plugin} />,
            className: 'pluginDialogOverlay'
        });
    };

    didSelectDeletePlugin = (event) => {
        let pluginId = getValueAtPathOfClosestParent(event.currentTarget, 'dataset.pluginId');
        let plugin = _.find(this.props.data.project.plugins, {id: pluginId});
        let actions = [
            {label: 'Cancel', handler: () => OverlayController.hideOverlay()},
            {label: 'Delete', secondary: true, type: "submit", handler: () => this.deletePlugin(plugin)}
        ];
        OverlayController.showOverlay({
            component: <Dialog showTitleBar={true} title={'Are you sure you want to delete this plugin?'} actions={actions}/>,
            className: 'pluginDialogDeleteOverlay'
        });
    };

    deletePlugin(plugin) {
        OverlayController.showActivityIndicator();
        this.props.deletePlugin(plugin.id).then(() => {
            OverlayController.hideActivityIndicator();
            OverlayController.hideOverlay();
        }).catch((error) => {
            OverlayController.hideActivityIndicator();
            OverlayController.showErrorAlert(error);
        });
    }

    renderTablePluginRows() {
        let statesById = _.keyBy(this.props.data.project.states, 'id');
        let plugins = this.props.data.project.plugins;
        if (this.props.reordering) {
            plugins = _.sortBy(plugins, plugin => this.props.indexOfItem(plugin.id))
        }
        return plugins.map(plugin => {
            let editColumns;
            let className = 'pluginRow';
            let style = {};
            if (this.props.reordering) {
                if (this.props.draggedItem === plugin.id) {
                    style = {transform: `translate(0px, ${this.props.translateY}px)`, zIndex: 10};
                    className += ' mdc-elevation--z10';
                }
                editColumns = [
                    <TableRowColumn key="editColumn1" className="editColumn"><i className="fa fa-sort editColumnButton" data-reorder-item={plugin.id} onMouseDown={this.props.onMouseDown}/></TableRowColumn>,
                    <TableRowColumn key="editColumn2" className="editColumn"/>
                ];
            } else {
                editColumns = [
                    <TableRowColumn key="editColumn1" className="editColumn"><IconButton className="editColumnButton" onTouchTap={this.didSelectEditPlugin}><EditIcon/></IconButton></TableRowColumn>,
                    <TableRowColumn key="editColumn2" className="editColumn"><IconButton className="editColumnButton" onTouchTap={this.didSelectDeletePlugin}><DeleteIcon/></IconButton></TableRowColumn>
                ]
            }
            return (
                <TableRow key={plugin.id} data-plugin-id={plugin.id} className={className} style={style}>
                    {editColumns}
                    <TableRowColumn>{PluginsManager.getPluginByTypeId(plugin.typeId).pluginTypeName}</TableRowColumn>
                    <TableRowColumn>{plugin.title}</TableRowColumn>
                    <TableRowColumn>{plugin.useForAllStates ? 'All States' : plugin.stateIds.map(stateId => statesById[stateId].name).join(', ')}</TableRowColumn>
                </TableRow>
            );
        })
    }

    render() {
        if (this.props.data.loading) {
            return (
                <CircularProgress className="activityIndicator"/>
            )
        } else if (this.props.data.error) {
            return <pre>{GraphQLStackTrace(this.props.data.error)}</pre>;
        }
        let table = null;
        if (this.props.data.project.plugins.length) {
            table = (
                <Table selectable={false}>
                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <TableRow>
                            {!this.props.reordering ? [
                                <TableHeaderColumn key="editColumn1" className="editColumn"><IconButton className="editColumnButton" onTouchTap={this.onSortClick}><i className="fa fa-sort"/></IconButton></TableHeaderColumn>,
                                <TableHeaderColumn key="editColumn2" className="editColumn"/>
                            ] : [
                                <TableHeaderColumn key="editColumn1" className="editColumn"><IconButton className="editColumnButton" onTouchTap={this.onCancelSortClick}><i className="fa fa-times"/></IconButton></TableHeaderColumn>,
                                <TableHeaderColumn key="editColumn2" className="editColumn"><IconButton className="editColumnButton" onTouchTap={this.onSaveSortClick}><i className="fa fa-check"/></IconButton></TableHeaderColumn>
                            ]}
                            <TableHeaderColumn>Plugin Type</TableHeaderColumn>
                            <TableHeaderColumn>Plugin Title</TableHeaderColumn>
                            <TableHeaderColumn>Plugin States</TableHeaderColumn>
                        </TableRow>
                    </TableHeader>
                    <TableBody displayRowCheckbox={false}>
                        {this.renderTablePluginRows()}
                    </TableBody>
                </Table>
            );
        }
        return (
            <div className="pluginsContainer">
                <h2>Plugins</h2>
                <RaisedButton className="addPluginButton" label="Add Plugin" primary={true} onTouchTap={this.didSelectAddPlugin.bind(this)}/>
                {table}
            </div>
        );
    }
});



// WEBPACK FOOTER //
// ./src/components/plugins/Plugins.js